import type { EnvironmentId } from "@cmmd-forge/contracts";
import type { Project } from "~/types";

export interface PullRequestHandoffExecutionCapabilities {
  readonly capabilities: {
    readonly runtime?: {
      readonly executionPlane: boolean;
    };
  };
}

export interface PullRequestHandoffPrimaryEnvironment extends PullRequestHandoffExecutionCapabilities {
  readonly environmentId: EnvironmentId;
  readonly label?: string;
}

export interface PullRequestHandoffSavedRuntime {
  readonly connectionState: string;
  readonly authState: string;
  readonly descriptor:
    | (PullRequestHandoffExecutionCapabilities & { readonly label?: string })
    | null;
}

export function pullRequestHandoffEnvironmentLabels(input: {
  readonly primary: PullRequestHandoffPrimaryEnvironment | null;
  readonly savedRuntimeById: Readonly<Record<EnvironmentId, PullRequestHandoffSavedRuntime>>;
}): ReadonlyMap<EnvironmentId, string> {
  const labels = new Map<EnvironmentId, string>();
  if (input.primary?.label?.trim()) labels.set(input.primary.environmentId, input.primary.label);
  for (const [environmentId, runtime] of Object.entries(input.savedRuntimeById)) {
    const label = runtime.descriptor?.label?.trim();
    if (label) labels.set(environmentId as EnvironmentId, label);
  }
  return labels;
}

export function pullRequestHandoffProjectKey(project: Project): string {
  return `${project.environmentId}:${project.id}`;
}

/**
 * How a checkout is named where the reader has to tell two of them apart. The project name
 * carries the difference — two rows are usually the same repository on the same environment — and
 * the environment is appended only where it adds something.
 */
export function pullRequestHandoffTargetLabel(
  project: Project,
  environmentLabels: ReadonlyMap<EnvironmentId, string>,
): string {
  const environmentLabel = environmentLabels.get(project.environmentId)?.trim();
  return environmentLabel ? `${project.name} · ${environmentLabel}` : project.name;
}
